// Add increment/decrement for items already in cart
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";

const API_URL = "https://accessoriesgirly.onrender.com/api/products";

export default function ProductInfoPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [mainImage, setMainImage] = useState("");
  const [loading, setLoading] = useState(true);

  // Load cart from localStorage
  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
    setCart(savedCart);
  }, []);

  // Fetch product & related products
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/${id}`);
        const productData = res.data;
        setProduct(productData);
        setMainImage(productData.images?.[0] || "");

        // Filter related products from all products available in productData.allProducts
        // Replace productData.allProducts with wherever you have the full product list
        const related = (productData.allProducts || [])
          .filter(
            (p) =>
              p._id !== id && p.category._id === productData.category._id
          )
          .slice(0, 4); // limit 4 products
        setRelatedProducts(related);
      } catch (err) {
        console.error("Error fetching product:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);


  const updateCart = (updatedCart) => {
    setCart(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    window.dispatchEvent(new Event("storage"));
  };

  const addToCart = (item) => {
    const existing = cart.find((c) => c.id === item._id);
    let updatedCart;
    if (existing) {
      updatedCart = cart.map((c) =>
        c.id === item._id ? { ...c, quantity: c.quantity + 1 } : c
      );
    } else {
      updatedCart = [...cart, { ...item, id: item._id, quantity: 1 }];
    }
    updateCart(updatedCart);
  };

  const removeFromCart = (itemId) => {
    const updatedCart = cart
      .map((c) =>
        c.id === itemId ? { ...c, quantity: c.quantity - 1 } : c
      )
      .filter((c) => c.quantity > 0);
    updateCart(updatedCart);
  };

  const handleBuyNow = (item) => {
    addToCart(item);
    navigate("/cart");
  };

  const calculateDiscount = (price, offerPrice) => {
    if (!offerPrice || offerPrice >= price) return 0;
    return Math.round(((price - offerPrice) / price) * 100);
  };

  if (loading) return (
    <div className="flex justify-center items-center h-screen">
      <motion.div
        className="text-2xl font-semibold text-pink-600"
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: 1 }}
      >
        Loading...
      </motion.div>
    </div>
  );
  if (!product) return (
    <div className="flex justify-center items-center h-screen">
      <p className="text-2xl font-semibold text-red-600">Product not found</p>
    </div>
  );

  const discount = calculateDiscount(product.price, product.offerPrice);
  const productInCart = cart.find((c) => c.id === product._id);

  return (
    <section className="py-12 bg-gradient-to-br from-pink-50 to-purple-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Left: Images */}
          <div className="lg:w-1/2 w-full">
            <motion.img
              src={mainImage}
              alt={product.name}
              className="w-full h-auto object-cover rounded-2xl shadow-xl mb-4 transition-transform duration-300 hover:scale-105"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            />
            <div className="flex gap-2 overflow-x-auto pb-2">
              {product.images?.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`${product.name} ${idx}`}
                  className={`w-20 h-20 object-cover rounded-lg cursor-pointer border-2 transition-all duration-200 ${mainImage === img ? "border-pink-600 shadow-md" : "border-gray-200 hover:border-pink-400"
                    }`}
                  onClick={() => setMainImage(img)}
                />
              ))}
            </div>
          </div>

          {/* Right: Product Info */}
          <motion.div
            className="lg:w-1/2 w-full bg-white rounded-2xl shadow-lg p-6 lg:p-8 flex flex-col justify-between"
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-pink-700 mb-3">{product.name}</h1>
              <p className="text-gray-600 text-sm sm:text-base mb-3">Category: {product.category.name}</p>
              <p className={`font-semibold text-lg mb-4 ${product.qty > 0 ? "text-green-600" : "text-red-600"}`}>
                {product.qty > 0 ? "In Stock" : "Out of Stock"}
              </p>
              <div className="flex items-center gap-3 mb-4">
                <p className="text-2xl sm:text-3xl font-bold text-gray-800">
                  {product.offerPrice && product.offerPrice < product.price ? (
                    <>
                      <span className="line-through text-gray-400 text-lg mr-2">
                        ₹{product.price.toFixed(2)}
                      </span>
                      <span>₹{product.offerPrice.toFixed(2)}</span>
                    </>
                  ) : (
                    <span>₹{product.price.toFixed(2)}</span>
                  )}
                </p>
                {discount > 0 && (
                  <span className="bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                    {discount}% OFF
                  </span>
                )}
              </div>
              <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-6">{product.description}</p>
            </div>

            {/* Cart Controls */}
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              {productInCart ? (
                <div className="flex items-center gap-4">
                  <button
                    className="bg-gray-300 px-4 py-2 rounded-lg"
                    onClick={() => removeFromCart(product._id)}
                  >
                    -
                  </button>
                  <span className="font-bold">{productInCart.quantity}</span>
                  <button
                    className="bg-pink-600 text-white px-4 py-2 rounded-lg"
                    onClick={() => addToCart(product)}
                  >
                    +
                  </button>
                  <motion.button
                    className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold"
                    onClick={() => handleBuyNow(product)}
                  >
                    Buy Now
                  </motion.button>
                </div>
              ) : (
                <>
                  <motion.button
                    className={`flex-1 py-3 px-6 text-white text-lg font-semibold rounded-lg transition-colors ${product.qty > 0 ? "bg-pink-600 hover:bg-pink-700" : "bg-gray-400 cursor-not-allowed"
                      }`}
                    onClick={() => product.qty > 0 && addToCart(product)}
                  >
                    Add to Cart
                  </motion.button>
                  <motion.button
                    className={`flex-1 py-3 px-6 text-white text-lg font-semibold rounded-lg transition-colors ${product.qty > 0 ? "bg-green-600 hover:bg-green-700" : "bg-gray-400 cursor-not-allowed"
                      }`}
                    onClick={() => product.qty > 0 && handleBuyNow(product)}
                  >
                    Buy Now
                  </motion.button>
                </>
              )}
            </div>
          </motion.div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-pink-700 mb-8 text-center">Related Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {relatedProducts.map((item) => {
                const itemDiscount = calculateDiscount(item.price, item.offerPrice);
                const inCart = cart.find((c) => c.id === item._id);

                return (
                  <motion.div
                    key={item._id}
                    className="relative bg-white border border-pink-200 shadow-md rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-2xl cursor-pointer"
                    whileHover={{ scale: 1.05 }}
                    onClick={() => navigate(`/productInfo/${item._id}`)}
                  >
                    {itemDiscount > 0 && (
                      <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                        {itemDiscount}% OFF
                      </span>
                    )}
                    <img
                      src={item.images?.[0] || "https://via.placeholder.com/300x300"}
                      alt={item.name}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-4 text-center">
                      <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2">{item.name}</h3>
                      <p className={`font-bold mb-2 ${item.qty > 0 ? "text-green-600" : "text-red-600"}`}>
                        {item.qty > 0 ? "In Stock" : "Out of Stock"}
                      </p>
                      <p className="text-pink-600 font-bold mb-3">
                        {item.offerPrice && item.offerPrice < item.price ? (
                          <>
                            <span className="line-through text-gray-400 text-sm mr-2">₹{item.price.toFixed(2)}</span>
                            <span>₹{item.offerPrice.toFixed(2)}</span>
                          </>
                        ) : (
                          <span>₹{item.price.toFixed(2)}</span>
                        )}
                      </p>

                      {/* Cart Controls for Related Products */}
                      {inCart ? (
                        <div className="flex justify-center items-center gap-2">
                          <button
                            className="bg-gray-300 px-3 py-1 rounded-lg"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFromCart(item._id);
                            }}
                          >
                            -
                          </button>
                          <span className="font-bold">{inCart.quantity}</span>
                          <button
                            className="bg-pink-600 text-white px-3 py-1 rounded-lg"
                            onClick={(e) => {
                              e.stopPropagation();
                              addToCart(item);
                            }}
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <motion.button
                          className={`w-full py-2 px-4 text-white text-sm font-semibold rounded-lg ${item.qty > 0 ? "bg-pink-600 hover:bg-pink-700" : "bg-gray-400 cursor-not-allowed"
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (item.qty > 0) addToCart(item);
                          }}
                        >
                          Add to Cart
                        </motion.button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
